// Brand dashboard: welcome and creator match list (V1) with platform/niche/location filters.

import React, { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig.js';
import { useAuth } from '../context/AuthContext.jsx';
import { getCreatorMatchCandidates } from '../lib/brandMatching';
import { TOP_NICHES } from '../constants/niches';
import BrandTopTabs from '../components/brand/BrandTopTabs.jsx';
import OnboardingLoading from '../components/onboarding/OnboardingLoading.jsx';
import { TikTokIcon } from '../components/icons/TikTokIcon';
import { InstagramIcon } from '../components/icons/InstagramIcon';
import { YouTubeIcon } from '../components/icons/YouTubeIcon';

const PLATFORM_ICON_MAP = {
  tiktok: TikTokIcon,
  instagram: InstagramIcon,
  youtube: YouTubeIcon,
};

const PLATFORM_OPTIONS = [
  { value: 'TikTok', label: 'TikTok' },
  { value: 'Instagram', label: 'Instagram' },
];

function normalizePlatformForFilter(name) {
  const n = (name || '').toLowerCase();
  if (n === 'tiktok') return 'tiktok';
  if (n === 'instagram') return 'instagram';
  return n;
}

function getCandidatePlatformNames(candidate) {
  const names = candidate.platformNames;
  if (Array.isArray(names) && names.length) return names;
  const platforms = candidate.platforms;
  if (!Array.isArray(platforms)) return [];
  return platforms
    .map((p) => p?.platformName && normalizePlatformForFilter(p.platformName))
    .filter(Boolean);
}

function passesPlatformFilter(candidate, selectedPlatforms) {
  if (!selectedPlatforms?.length) return true;
  const creatorPlatforms = new Set(getCandidatePlatformNames(candidate).map(normalizePlatformForFilter));
  const selected = selectedPlatforms.map(normalizePlatformForFilter).filter(Boolean);
  return selected.every((p) => creatorPlatforms.has(p));
}

function passesNicheFilter(candidate, selectedNiches) {
  if (!selectedNiches?.length) return true;
  const creatorNiches = (candidate.niches || []).map((n) => (n || '').trim().toLowerCase()).filter(Boolean);
  const selectedSet = new Set(selectedNiches.map((n) => (n || '').trim().toLowerCase()).filter(Boolean));
  return creatorNiches.some((n) => selectedSet.has(n));
}

function passesLocationFilter(candidate, locationFilter) {
  const term = (locationFilter || '').trim();
  if (!term) return true;
  const loc = (candidate.location || '').trim();
  if (!loc) return false;
  return loc.toLowerCase().includes(term.toLowerCase());
}

/** Normalize niches for display: split comma-separated strings so each niche is its own chip. */
function getDisplayNiches(candidate) {
  const raw = candidate.niches;
  if (!Array.isArray(raw) && typeof raw !== 'string') return [];
  const arr = Array.isArray(raw) ? raw : [raw];
  const out = [];
  for (const item of arr) {
    const s = String(item || '').trim();
    if (!s) continue;
    if (s.includes(',')) {
      out.push(...s.split(',').map((n) => n.trim()).filter(Boolean));
    } else {
      out.push(s);
    }
  }
  return out;
}

/** Follower count from all sources (legacy manual, platformStats, platforms array). Used for display and completeness. */
function getTotalFollowersForCandidate(candidate) {
  let n = Number(candidate.stats?.totalFollowers);
  if (Number.isFinite(n) && n > 0) return n;
  if (candidate.platformStats && typeof candidate.platformStats === 'object' && !Array.isArray(candidate.platformStats)) {
    n = Object.values(candidate.platformStats).reduce((sum, p) => sum + (Number(p?.followers) || Number(p?.followersCount) || 0), 0);
    if (n > 0) return n;
  }
  if (Array.isArray(candidate.platforms)) {
    n = candidate.platforms.reduce((sum, p) => sum + (Number(p?.followers) || 0), 0);
    if (n > 0) return n;
  }
  return 0;
}

/** Score 0-6: how many card fields are filled (profile image, name, handle, niches, location, followers). Higher = more complete. */
function getCardCompletenessScore(candidate) {
  let score = 0;
  if (candidate.profileImageUrl && String(candidate.profileImageUrl).trim()) score += 1;
  if (candidate.displayName && String(candidate.displayName).trim()) score += 1;
  if (candidate.username && String(candidate.username).trim()) score += 1;
  if (getDisplayNiches(candidate).length > 0) score += 1;
  if (candidate.location && String(candidate.location).trim()) score += 1;
  if (getTotalFollowersForCandidate(candidate) > 0) score += 1;
  return score;
}

const BrandDashboard = ({ onNavigate }) => {
  const { user, profile, loading: authLoading } = useAuth();
  const [brandProfile, setBrandProfile] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [filterPlatforms, setFilterPlatforms] = useState([]);
  const [filterNiches, setFilterNiches] = useState([]);
  const [filterLocation, setFilterLocation] = useState('');
  const [searching, setSearching] = useState(false);

  const role = profile?.role ?? profile?.userType;
  const isBrand = role === 'brand';

  const hasPreferences = preferences && (
    (preferences.platforms && preferences.platforms.length > 0) ||
    (preferences.niches && preferences.niches.length > 0)
  );

  // Show "Set your preferences" only when no preferences AND no candidates (so they see the list once we've loaded creators)
  const showNoPreferences = !loading && !hasPreferences && candidates.length === 0;

  useEffect(() => {
    if (!user || !isBrand) return;
    let cancelled = false;
    (async () => {
      try {
        const brandSnap = await getDoc(doc(db, 'brandProfiles', user.uid));
        const data = brandSnap.exists() ? brandSnap.data() : {};
        const prefs = data.preferences || { platforms: [], niches: [] };
        if (cancelled) return;
        setBrandProfile(data);
        setPreferences(prefs);
        setFilterPlatforms([]);
        setFilterNiches([]);
        setFilterLocation('');

        // Always fetch discoverable creators (even with empty preferences) so the list can show everyone for now.
        const list = await getCreatorMatchCandidates(prefs);
        if (cancelled) return;
        setCandidates(list);
      } catch (err) {
        if (!cancelled) {
          console.error('[BrandDashboard] Load failed:', err);
          setError('Failed to load creators.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, isBrand]);

  const filteredCandidates = useMemo(() => {
    const filtered = candidates.filter((c) => {
      if (!passesPlatformFilter(c, filterPlatforms)) return false;
      if (!passesNicheFilter(c, filterNiches)) return false;
      if (!passesLocationFilter(c, filterLocation)) return false;
      return true;
    });
    return [...filtered].sort((a, b) => getCardCompletenessScore(b) - getCardCompletenessScore(a));
  }, [candidates, filterPlatforms, filterNiches, filterLocation]);

  const hasActiveFilters =
    filterPlatforms.length > 0 ||
    filterNiches.length > 0 ||
    (filterLocation && filterLocation.trim().length > 0);

  const handleClearFilters = () => {
    setFilterPlatforms([]);
    setFilterNiches([]);
    setFilterLocation('');
  };

  const handleSearch = async () => {
    if (!user || !preferences || !hasPreferences) return;
    setSearching(true);
    setError('');
    try {
      const list = await getCreatorMatchCandidates(preferences);
      setCandidates(list);
    } catch (err) {
      console.error('[BrandDashboard] Search failed:', err);
      setError('Failed to load creators.');
    } finally {
      setSearching(false);
    }
  };

  const togglePlatform = (platform) => {
    setFilterPlatforms((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]
    );
  };

  const toggleNiche = (niche) => {
    setFilterNiches((prev) => {
      if (prev.includes(niche)) return prev.filter((n) => n !== niche);
      if (prev.length >= 3) return prev;
      return [...prev, niche];
    });
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      onNavigate('/login', null, { replace: true });
      return;
    }
    if (!isBrand && profile) {
      onNavigate('/profile/edit', null, { replace: true });
      return;
    }
  }, [user, profile, authLoading, isBrand, onNavigate]);

  if (authLoading || (user && !profile && !isBrand)) {
    return (
      <OnboardingLoading
        label="Loading"
        sublabel="Loading your dashboard."
        progressPercent={50}
      />
    );
  }
  if (!user || !isBrand) return null;

  const showNoMatchesYet = !loading && hasPreferences && candidates.length === 0 && !error;
  const showNoCreatorsMatchFilters = !loading && hasPreferences && candidates.length > 0 && filteredCandidates.length === 0 && hasActiveFilters;

  return (
    <div className="min-h-screen bg-gray-50">
      <BrandTopTabs
        activeTab="dashboard"
        brandProfile={brandProfile}
        onNavigate={onNavigate}
      />

      <main className="max-w-5xl mx-auto px-4 py-6 sm:px-6">
        <div className="mb-2">
          <h1 className="text-xl font-semibold text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500">Here are creators that match your criteria.</p>
        </div>

        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {!showNoPreferences && (
          <section className="mb-6 rounded-xl border border-slate-200 bg-white p-4">
            <h2 className="text-sm font-medium text-slate-700 mb-3">Filters</h2>
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Platform</label>
                <div className="flex flex-wrap gap-2">
                  {PLATFORM_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => togglePlatform(opt.value)}
                      className={`rounded-lg border px-3 py-1.5 text-sm font-medium ${
                        filterPlatforms.includes(opt.value)
                          ? 'border-[#1B9DA2] bg-[#1B9DA2]/10 text-[#1B9DA2]'
                          : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="w-full sm:w-auto">
                <label className="block text-xs font-medium text-slate-500 mb-1">Niche</label>
                <div className="flex flex-wrap gap-2 max-w-md">
                  {TOP_NICHES.map((niche) => {
                    const selected = filterNiches.includes(niche);
                    const disabled = !selected && filterNiches.length >= 3;
                    return (
                      <button
                        key={niche}
                        type="button"
                        disabled={disabled}
                        onClick={() => toggleNiche(niche)}
                        className={`rounded-lg border px-2.5 py-1 text-xs font-medium ${
                          selected
                            ? 'border-[#1B9DA2] bg-[#1B9DA2]/10 text-[#1B9DA2]'
                            : disabled
                              ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                              : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {niche}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-400 mt-0.5">Max 3 selected.</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Location</label>
                <input
                  type="text"
                  value={filterLocation}
                  onChange={(e) => setFilterLocation(e.target.value)}
                  placeholder="e.g. San Francisco, CA"
                  className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900 placeholder:text-slate-400 min-w-[200px]"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={searching}
                  className="rounded-lg bg-[#1B9DA2] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#168589] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {searching ? 'Searching...' : 'Search'}
                </button>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>
          </section>
        )}

        {loading ? (
          <p className="text-slate-600">Loading creators...</p>
        ) : showNoPreferences ? (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
            <h3 className="text-lg font-semibold text-slate-900">Set your preferences</h3>
            <p className="mt-2 text-slate-600">
              Choose niches, platforms, and location to see creators that match.
            </p>
            <button
              type="button"
              onClick={() => onNavigate('/brand/preferences', null, { replace: false })}
              className="mt-4 rounded-lg bg-[#1B9DA2] px-4 py-2 text-sm font-medium text-white hover:bg-[#168589]"
            >
              Update preferences
            </button>
          </div>
        ) : showNoMatchesYet ? (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
            <h3 className="text-lg font-semibold text-slate-900">No matches yet</h3>
            <p className="mt-2 text-slate-600">
              Try broadening your preferences or adjusting filters.
            </p>
            <button
              type="button"
              onClick={handleClearFilters}
              className="mt-4 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Clear filters
            </button>
          </div>
        ) : showNoCreatorsMatchFilters ? (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
            <h3 className="text-lg font-semibold text-slate-900">No creators match these filters.</h3>
            <button
              type="button"
              onClick={handleClearFilters}
              className="mt-4 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <ul className="grid gap-4 sm:grid-cols-2">
            {filteredCandidates.map((c) => {
              const hasUsername = !!c.username;
              const profilePath = hasUsername ? `/u/${c.username}` : null;
              const platformNames = getCandidatePlatformNames(c);
              const totalFollowers = getTotalFollowersForCandidate(c);
              return (
                <li key={c.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => profilePath && onNavigate(profilePath, null, { replace: false })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (profilePath) onNavigate(profilePath, null, { replace: false });
                      }
                    }}
                    className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col gap-3 shadow-sm hover:border-slate-300 cursor-pointer"
                  >
                    <div className="flex items-start gap-3">
                      {c.profileImageUrl ? (
                        <img
                          src={c.profileImageUrl}
                          alt=""
                          className="h-12 w-12 rounded-full object-cover bg-slate-100 flex-shrink-0"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 text-sm font-medium flex-shrink-0">
                          {(c.displayName || c.username || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900 truncate">
                          {c.displayName || c.username || 'Creator'}
                        </p>
                        {c.username && (
                          <p className="text-sm text-slate-500 truncate">@{c.username}</p>
                        )}
                      </div>
                      {Array.isArray(platformNames) && platformNames.length > 0 && (
                        <div className="flex items-center gap-1.5 flex-shrink-0" aria-label="Connected platforms">
                          {platformNames.map((p) => {
                            const Icon = PLATFORM_ICON_MAP[p.toLowerCase()];
                            if (!Icon) return null;
                            return (
                              <span
                                key={p}
                                className="flex items-center justify-center w-6 h-6 text-slate-400"
                                title={p.charAt(0).toUpperCase() + p.slice(1)}
                                aria-hidden
                              >
                                <Icon className="w-5 h-5" />
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {getDisplayNiches(c).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {getDisplayNiches(c).slice(0, 5).map((n) => (
                          <span
                            key={`${n}-${c.id}`}
                            className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
                          >
                            {n}
                          </span>
                        ))}
                      </div>
                    )}
                    {c.location && (
                      <p className="text-xs text-slate-500 truncate">{c.location}</p>
                    )}
                    {totalFollowers > 0 && (
                      <p className="text-xs text-slate-500">
                        {totalFollowers >= 1e6
                          ? `${(totalFollowers / 1e6).toFixed(1)}M followers`
                          : totalFollowers >= 1e3
                            ? `${(totalFollowers / 1e3).toFixed(1)}K followers`
                            : `${totalFollowers} followers`}
                      </p>
                    )}
                    <button
                      type="button"
                      title={!hasUsername ? 'Username not set' : undefined}
                      disabled={!hasUsername}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (profilePath) onNavigate(profilePath, null, { replace: false });
                      }}
                      className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 self-start disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      View profile
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
};

export default BrandDashboard;
